/* ---------------------------------------------------------------------------
   People & roles — who may use this register, and as what.

   The one administration screen this application has. Everything else on the
   desk shell is the register; this is the application itself.

   WHO SEES IT. Only somebody who holds `people.manage`, which is only an
   administrator. Not disabled for everybody else — absent. A greyed-out
   destination in the nav pane is an invitation to ask why, repeatedly, and there
   is nothing on this screen an inspector could act on if they got here. The nav
   pane withholds the entry (src/components/navpane.ts) and this screen refuses to
   draw the list for anyone the register did not grant it to, because a pasted
   `#/people` link reaches a screen the pane never drew.

   IT USED TO HAVE A SECOND PANEL, and the shape of what replaced it is worth
   recording. While Cloudflare Access was the authenticator there were two
   perimeters to administer — the Access allow list, which decided who reached
   the application at all, and this register's own roles, which decided what they
   could do once in. The screen managed both, and had to explain the difference
   constantly. Access is gone: signing in is this register's own
   (functions/api/auth/login.ts), so there is one perimeter and one panel, and an
   account is a username, its roles and a password rather than a row in two systems
   that had to be kept in step by hand. Since LC-07 an account holds any number
   of roles, so the role picker is a checkbox per role, with the suspension
   beside them — a state of the account that keeps its roles, not a ninth role.

   WHY IT IS A SCREEN AND NOT A DIALOG. Two reasons, and the second is the one
   that decided it. A dialog is for a thing you are doing to the record behind it,
   and this is not about any record. And the list has to be readable next to the
   Guide's explanation of what the roles mean — somebody deciding whether a
   colleague should be an engineer or an inspector has a question this screen
   cannot answer on its own, and a modal is exactly the wrong place to be when
   you need to go and read something.

   WHAT IT REFUSES TO PRETEND. Three things, and they are the whole reason this
   file has a header:

     - It does not say who has been using the application. There is no
       last-seen column in migration 0019 and the reason is written there: filling
       one in means a database write on every read. What it shows is who granted a
       role and when, which is the question an administrator is actually asked.
     - It does not treat a missing role as a refusal. An account with no roles
       reads the register, and the card at the top says so in as many words.
       Saying no is suspending.
     - It does not claim a division limits anybody. The field is a note about a
       person — see the header of src/data/roles.ts for why a district-scoped role
       was not half-built — and the label says so where somebody typing into it
       will read it.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, canManagePeople, type State } from '../core/store.ts';
import { button, card, cardHead, fact, field, tag } from '../components/widgets.ts';
import {
  CAPABILITIES,
  CAPABILITY_LABEL,
  ROLES,
  ROLE_COPY,
  capabilitiesOf,
  roleCan,
  rolesLabel,
  type Person,
  type Role,
} from '../data/roles.ts';

/**
 * Where each role sits on the shared monochrome ramp — see tag() in widgets.ts.
 *
 * The roles that decide — administering the register, accepting what others
 * made — are rank 2; the roles that record are rank 1. A suspended account is
 * rank 3, the loudest, and that is deliberate: it is the row an administrator
 * scanning this list most needs to notice, because it is actively refusing
 * somebody.
 */
const ROLE_RANK: Record<Role, 1 | 2> = {
  admin: 2,
  planner: 1,
  site: 1,
  resident: 2,
  inspector: 1,
  engineer: 2,
  rater: 1,
  approver: 2,
};

/** An account's roles as tags, or the one tag that says it holds none or is suspended. */
function roleTags(p: Pick<Person, 'roles' | 'suspended'>): HTMLElement {
  return h(
    'div',
    { class: 'ppl__tags' },
    p.suspended ? tag('Suspended', 3, 'May do nothing, including read. The roles are kept.') : null,
    ...(p.roles.length === 0
      ? [tag('No role', 1, 'Reads the register and writes nothing')]
      : p.roles.map((role) => tag(ROLE_COPY[role].label, ROLE_RANK[role], ROLE_COPY[role].who))),
  );
}

/** What to call somebody: their name where they gave one, their address otherwise. */
function personName(p: Person): string {
  return p.fullName ?? p.email;
}

/* --------------------------- the deployment --------------------------- */

/**
 * How this deployment decides who may do what, before any row of the table.
 *
 * First card on the screen because it answers the question the table below it
 * cannot: whether any of this is being enforced at all. A register behind no
 * Access application has an open API, the roles on the list mean nothing, and a
 * screen that showed the list without saying so would be actively misleading.
 */
function deploymentCard(s: Readonly<State>): HTMLElement {
  const identity = s.identity;
  const known = identity !== null;

  return card(
    { pad: true },
    cardHead('How this register decides', known ? 'Roles enforced' : undefined),

    fact(
      'Signed in as',
      known
        ? (identity.email ?? `Service token · ${identity.serviceToken}`)
        : s.backend === 'live'
          ? 'Nobody — this browser has no session'
          : 'The register has not been reached',
    ),
    fact(
      'Your roles',
      identity?.roles
        ? `${rolesLabel(identity.roles, identity.suspended)}${
            identity.roleSource === 'deployment'
              ? ' — administrator from this deployment’s ADMIN_EMAILS setting'
              : ''
          }`
        : '—',
    ),
    fact('An account with no role', 'Reads the register and writes nothing'),
    fact(
      'Administrators from the deployment',
      s.peopleDeploymentAdmins === 0
        ? 'None — every administrator is a row below'
        : `${s.peopleDeploymentAdmins}, set in ADMIN_EMAILS — the way back in if the roles ` +
          'below are ever misconfigured. Other roles may be granted to them here'
    ),
  );
}

/* --------------------------- what has been done --------------------------- */

/**
 * The append-only admin log — migration 0020.
 *
 * WHAT IT ANSWERS THAT NOTHING ELSE COULD. The rows above show who holds what
 * NOW; `users` keeps one decision of history — `granted_by` and `granted_at` are
 * overwritten by the next change — so "who made this person an engineer, and
 * when" stopped being answerable the moment somebody changed it again.
 *
 * IT STILL RENDERS `access` ENTRIES, and that is deliberate rather than
 * leftover. Nothing writes them any more: they came from the panel that managed
 * the Cloudflare Access allow list, which went when Access did. But this table is
 * append-only, so any row written while that panel existed is still here and
 * still true — and a log that quietly stopped displaying a kind of entry it
 * contains would be a log that lies by omission. The chip tells them apart.
 *
 * Bounded to the most recent entries on purpose: this is a panel answering "what
 * has been done lately", not an export.
 */
function logCard(s: Readonly<State>): HTMLElement {
  return card(
    { pad: true },
    cardHead('Recently', s.adminLog.length ? `${s.adminLog.length} entries` : undefined),
    s.adminLog.length === 0
      ? h('p', {
          class: 'ppl__none',
          text:
            'Nothing recorded yet. Every change to a role, and every password issued, is written ' +
            'here with who did it — from this point on. It cannot be edited.',
        })
      : h(
          'table',
          { class: 'tbl ppl__tbl' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'width:100px', text: 'When' }),
              h('th', { style: 'width:80px', text: 'What' }),
              h('th', { text: 'Who' }),
              h('th', { text: 'By' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...s.adminLog.map((entry) =>
              h(
                'tr',
                {},
                h('td', { class: 'ppl__granted', text: entry.at.slice(0, 10) }),
                h(
                  'td',
                  {},
                  tag(
                    entry.kind === 'access' ? 'Access' : 'Role',
                    entry.kind === 'access' ? 3 : 2,
                    entry.kind === 'access'
                      ? 'From the Cloudflare Access allow list, which this register no longer manages'
                      : 'What they may do, and their password',
                  ),
                ),
                h(
                  'td',
                  {},
                  h('div', { class: 'ppl__name', text: entry.subject }),
                  h('div', {
                    class: 'ppl__mail',
                    text: entry.detail ? `${entry.action} · ${entry.detail}` : entry.action,
                  }),
                ),
                h('td', { class: 'ppl__by', text: entry.actor ?? 'not attributable' }),
              ),
            ),
          ),
        ),
  );
}

/* --------------------------- the form --------------------------- */

/**
 * Granting a role, and changing one.
 *
 * Inline above the list rather than in a modal, for the reason the file header
 * gives: the answer to "engineer or inspector?" is on the same screen, and a
 * dialog is the wrong place to be when you need to go and read it.
 *
 * The address is read-only while editing — see `personEditing` in the store. An
 * identity is the key, and letting it be retyped mid-edit would quietly create a
 * second row and leave the first one holding the role somebody meant to change.
 */
function grantForm(s: Readonly<State>): HTMLElement | null {
  const draft = s.personDraft;
  if (!draft) return null;

  const editing = s.personEditing !== null;
  /* An administrator from ADMIN_EMAILS: their `admin` and their suspension are
     the deployment's, so both are drawn fixed; every other role is theirs to be
     granted like anybody's. */
  const fromDeployment =
    editing && (s.people ?? []).some((p) => p.email === s.personEditing && p.fromDeployment);
  const may = capabilitiesOf(draft.roles, draft.suspended);

  return card(
    { pad: true, class: 'ppl__form' },
    cardHead(
      editing ? `Change the roles of ${s.personEditing}` : 'Add somebody to the register',
      editing ? undefined : 'One account per person',
    ),

    h(
      'div',
      { class: 'ppl__grid' },
      field(
        /* NOT "Verified address" any more. Nothing verifies an address: this is
           the username they will type into the sign-in form, and it may be a
           plain name. The label said otherwise for one commit, which is exactly
           long enough for somebody to have typed an email they did not need. */
        'Username',
        editing
          ? h('div', { class: 'ppl__fixed', text: s.personEditing ?? '' })
          : h('input', {
              id: 'ppl-email',
              class: 'input',
              type: 'text',
              value: draft.email,
              placeholder: 'r.thapa or r.thapa@dor.gov.np',
              autocomplete: 'off',
              spellcheck: 'false',
              oninput: (e: Event) =>
                actions.setPersonField('email', (e.target as HTMLInputElement).value),
            }),
        editing ? undefined : 'ppl-email',
      ),
      field(
        'Name',
        h('input', {
          id: 'ppl-name',
          class: 'input',
          type: 'text',
          value: draft.fullName,
          placeholder: 'As it should read on screen',
          oninput: (e: Event) =>
            actions.setPersonField('fullName', (e.target as HTMLInputElement).value),
        }),
        'ppl-name',
      ),
      field(
        'Division or district',
        h('input', {
          id: 'ppl-div',
          class: 'input',
          type: 'text',
          value: draft.division,
          placeholder: 'e.g. DoR Division Road Office, Chitwan',
          oninput: (e: Event) =>
            actions.setPersonField('division', (e.target as HTMLInputElement).value),
        }),
        'ppl-div',
      ),
    ),

    field(
      'Why',
      h('input', {
        id: 'ppl-note',
        class: 'input',
        type: 'text',
        value: draft.note,
        placeholder: 'For whoever inherits this register — e.g. "Bridge section, from Shrawan 2081"',
        oninput: (e: Event) =>
          actions.setPersonField('note', (e.target as HTMLInputElement).value),
      }),
      'ppl-note',
    ),

    /* ONE CHECKBOX PER ROLE, and the suspension beside them. An account holds
       as many roles as its holder has jobs; the ids are stable so a re-render
       keeps focus on the box just pressed. */
    h(
      'div',
      { class: 'ppl__roles', role: 'group', 'aria-label': 'Roles' },
      ...ROLES.map((role) => {
        const fixed = fromDeployment && role === 'admin';
        return h(
          'label',
          { class: 'ppl__role', title: ROLE_COPY[role].who },
          h('input', {
            id: `ppl-role-${role}`,
            type: 'checkbox',
            checked: fixed || draft.roles.includes(role),
            disabled: fixed,
            onchange: (e: Event) =>
              actions.setPersonRole(role, (e.target as HTMLInputElement).checked),
          }),
          ROLE_COPY[role].label,
        );
      }),
      fromDeployment
        ? null
        : h(
            'label',
            { class: ['ppl__role', 'ppl__role--off'], title: 'May do nothing, including read. The roles are kept.' },
            h('input', {
              id: 'ppl-suspended',
              type: 'checkbox',
              checked: draft.suspended,
              onchange: (e: Event) =>
                actions.setPersonSuspended((e.target as HTMLInputElement).checked),
            }),
            'Suspended',
          ),
    ),

    /* WHAT THE CHOSEN ROLES PERMIT TOGETHER, under the checkboxes and not on
       another screen. This is the one moment somebody is deciding, and a role
       name on its own carries none of the decision. Read from the same table
       the API enforces. */
    h(
      'div',
      { class: 'ppl__what' },
      h('div', { class: 'ppl__whathead', text: `${rolesLabel(draft.roles, draft.suspended)} may:` }),
      may.length === 0
        ? h('p', { class: 'ppl__none', text: 'Nothing, including reading.' })
        : h(
            'ul',
            { class: 'ppl__caps' },
            ...may.map((capability) => h('li', { text: CAPABILITY_LABEL[capability] })),
          ),
    ),

    h(
      'div',
      { class: 'ppl__actions' },
      button(s.personSaving ? 'Saving…' : editing ? 'Save the roles' : 'Create the account', {
        primary: true,
        tall: true,
        disabled: s.personSaving,
        onClick: () => void actions.submitPerson(),
      }),
      button('Cancel', {
        tall: true,
        disabled: s.personSaving,
        onClick: () => actions.closePersonForm(),
      }),
    ),
  );
}

/* --------------------------- the list --------------------------- */

/** One row of the table, with the confirmation folded in beneath it. */
function personRow(p: Person, s: Readonly<State>): HTMLElement[] {
  const confirming = s.personRemoving === p.email;

  const row = h(
    'tr',
    { class: p.suspended ? 'ppl__row--off' : undefined },
    h(
      'td',
      {},
      h('div', { class: 'ppl__name', text: personName(p) }),
      p.fullName ? h('div', { class: 'ppl__mail', text: p.email }) : null,
      p.note ? h('div', { class: 'ppl__note', text: p.note }) : null,
    ),
    h('td', {}, roleTags(p)),
    h('td', { class: 'ppl__div', text: p.division ?? '—' }),
    h(
      'td',
      { class: 'ppl__granted' },
      p.fromDeployment
        ? h('span', { title: 'Set in this deployment’s ADMIN_EMAILS', text: 'Deployment setting' })
        : h(
            'span',
            {},
            h('div', { text: p.grantedAt.slice(0, 10) || '—' }),
            h('div', { class: 'ppl__by', text: p.grantedBy ?? 'not attributable' }),
          ),
    ),
    h(
      'td',
      { class: 'ppl__tools' },
      /* A deployment administrator's `admin` is the setting's, outside this
         application, which is the whole point of it — but the other roles they
         work in are granted here like anybody's, so Edit is offered and the
         form draws `admin` fixed. */
      button('Edit', {
        sm: true,
        iconPath: ICONS.edit,
        title: `Change the roles of ${p.email}`,
        onClick: () => actions.openPersonEdit(p),
      }),
      /* THE OTHER HALF OF AN ACCOUNT. A role says what somebody may do; a
         password is how they get in to do it. There is no reset email from this
         application — no sender, no domain — so a forgotten password is an
         administrator generating one and reading it out, and this is the button
         that does it. */
      p.fromDeployment
        ? null
        : button('Issue password', {
            sm: true,
            title: `Generate a new password for ${p.email} and show it once`,
            onClick: () => void actions.issuePassword(p.email),
          }),
      p.fromDeployment
        ? null
        : button('Remove', {
            sm: true,
            danger: true,
            iconPath: ICONS.trash,
            title: `Remove the row for ${p.email}`,
            onClick: () => actions.askRemovePerson(p.email),
          }),
    ),
  );

  if (!confirming) return [row];

  /* THE CONFIRMATION SAYS WHAT REMOVING DOES NOT DO. This is the one place the
     application can catch the mistake that matters: an administrator reaching
     for Remove when they mean Suspend, and handing somebody read access back
     while believing they have cut it off. */
  return [
    row,
    h(
      'tr',
      { class: 'ppl__confirm' },
      h(
        'td',
        { colspan: '5' },
        h('div', {
          class: 'ppl__confirmtext',
          text:
            `Remove the account ${p.email} and its roles? If Cloudflare Access still admits ` +
            'them they can read the register. To deny access, suspend the account instead.',
        }),
        h(
          'div',
          { class: 'ppl__confirmrow' },
          button(s.personSaving ? 'Removing…' : 'Remove the row', {
            danger: true,
            sm: true,
            disabled: s.personSaving,
            onClick: () => void actions.removePerson(p.email),
          }),
          button('Suspend instead', {
            sm: true,
            disabled: s.personSaving,
            title: 'Keep the account and its roles, and suspend it, which does deny access',
            onClick: () => {
              actions.askRemovePerson(null);
              actions.openPersonEdit({ ...p, suspended: true });
            },
          }),
          button('Cancel', {
            sm: true,
            disabled: s.personSaving,
            onClick: () => actions.askRemovePerson(null),
          }),
        ),
      ),
    ),
  ];
}

function peopleList(s: Readonly<State>): HTMLElement {
  const people = s.people;

  return card(
    { pad: true },
    cardHead(
      'Roles granted',
      people ? `${people.length} ${people.length === 1 ? 'identity' : 'identities'}` : undefined,
      s.personDraft
        ? null
        : button('Grant a role', {
            sm: true,
            primary: true,
            iconPath: ICONS.plus,
            onClick: () => actions.openGrant(),
          }),
      button(s.peopleLoading ? 'Reading…' : 'Refresh', {
        sm: true,
        iconPath: ICONS.refresh,
        disabled: s.peopleLoading,
        onClick: () => void actions.refreshPeople(),
      }),
    ),

    s.peopleNote ? h('p', { class: 'ppl__ok', text: s.peopleNote }) : null,
    s.peopleError ? h('p', { class: 'ppl__err', text: s.peopleError }) : null,

    /* NULL AND EMPTY ARE DIFFERENT ANSWERS. "Nobody has been granted a role" is a
       real and correct state for a new register; "we could not read the list" is
       a failure. Showing an empty table for both is the exact thing this
       application does not do anywhere else. */
    people === null
      ? h('p', {
          class: 'ppl__none',
          text: s.peopleLoading
            ? 'Reading the list…'
            : 'The list has not been read. Refresh to try again.',
        })
      : people.length === 0
        ? h('p', {
            class: 'ppl__none',
            text: 'No accounts yet. An account with no role reads the register and writes nothing.',
          })
        : h(
            'table',
            { class: 'tbl ppl__tbl' },
            h(
              'thead',
              {},
              h(
                'tr',
                {},
                h('th', { text: 'Person' }),
                h('th', { style: 'width:220px', text: 'Roles' }),
                h('th', { style: 'width:190px', text: 'Division' }),
                h('th', { style: 'width:150px', text: 'Granted' }),
                h('th', { style: 'width:170px', text: '' }),
              ),
            ),
            h('tbody', {}, ...people.flatMap((p) => personRow(p, s))),
          ),
  );
}

/**
 * A password that has just been issued, shown once.
 *
 * ONCE IS THE WHOLE DESIGN. The register stored a hash and cannot show this
 * again, and the panel says so where somebody about to close it will read it —
 * an administrator who assumes they can come back for it has the wrong model of
 * what was kept. Issuing another costs nothing, which is the correct answer and
 * is better than a register that can tell you somebody's password.
 *
 * Deliberately loud and deliberately not dismissible by clicking away: it is the
 * only moment this string exists anywhere outside the head of the person reading
 * it out.
 */
function issuedCard(s: Readonly<State>): HTMLElement | null {
  const issued = s.issuedPassword;
  if (!issued) return null;

  return card(
    { pad: true, class: 'ppl__form' },
    cardHead('New password for ' + issued.email, 'shown once'),
    h('p', { class: 'ppl__issued', text: issued.password }),
    h('p', { class: 'ppl__hint', style: 'margin-top:8px', text: issued.note }),
    h(
      'div',
      { class: 'ppl__actions' },
      button('I have written it down', {
        primary: true,
        onClick: () => actions.clearIssuedPassword(),
      }),
    ),
  );
}

/* --------------------------- the reference --------------------------- */

/**
 * The whole permission table, on one grid.
 *
 * Generated from ROLE_CAPABILITIES rather than written out, which is the
 * opposite of the rule the Guide follows about the nav pane — and correct here
 * for the opposite reason. The Guide explains the roles in prose to somebody
 * meeting them; this is an administrator checking what a change will do, and a
 * grid that could disagree with what the API enforces would be worse than no
 * grid. The Guide's prose is checked against this table in `npm test`.
 */
function matrix(): HTMLElement {
  return card(
    { pad: true },
    cardHead('What each role may do', `${CAPABILITIES.length} permissions`),
    h(
      'div',
      { class: 'ppl__matrixwrap' },
      h(
        'table',
        { class: 'tbl ppl__matrix' },
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { text: 'May…' }),
            ...ROLES.map((role) =>
              h('th', { class: 'ppl__mcol', title: ROLE_COPY[role].who, text: ROLE_COPY[role].label }),
            ),
          ),
        ),
        h(
          'tbody',
          {},
          ...CAPABILITIES.map((capability) =>
            h(
              'tr',
              {},
              h('td', { class: 'ppl__mrow', text: CAPABILITY_LABEL[capability] }),
              ...ROLES.map((role) =>
                h('td', {
                  class: ['ppl__mcell', roleCan(role, capability) && 'ppl__mcell--yes'],
                  /* A word, not a tick. This grid is read by people deciding
                     something, sometimes printed, and a glyph that means "yes"
                     to whoever wrote the CSS means nothing at all through a
                     screen reader or a photocopier. */
                  text: roleCan(role, capability) ? 'Yes' : '—',
                }),
              ),
            ),
          ),
        ),
      ),
    ),
  );
}

/* --------------------------- the screen --------------------------- */

export function peopleScreen(s: Readonly<State>): HTMLElement {
  /* THE REFUSAL. The nav pane does not draw this destination for somebody who
     cannot manage people, and a pasted address reaches it anyway — so the screen
     says no on its own authority rather than relying on the pane having been
     drawn. It is a plain statement and not an error: arriving here without the
     role is somebody following a link, not a fault. */
  if (!canManagePeople(s)) {
    return h(
      'div',
      { class: 'ppl' },
      card(
        { pad: true },
        cardHead('People & roles'),
        h('p', {
          class: 'ppl__none',
          text:
            `Managing who may use this register is an administrator's screen, and your roles` +
            `${s.identity?.roles ? ` (${rolesLabel(s.identity.roles)})` : ''} do not carry ` +
            'it. Everything you can do is on the other screens; the Guide explains what each ' +
            'role is for.',
        }),
        h(
          'div',
          { class: 'ppl__actions' },
          button('Open the guide', {
            primary: true,
            iconPath: ICONS.book,
            onClick: () => actions.go('guide'),
          }),
        ),
      ),
    );
  }

  return h(
    'div',
    { class: 'ppl' },
    h(
      'div',
      { class: 'ppl__lead' },
      h('span', { class: 'ppl__leadicon' }, icon(ICONS.people, { size: 18 })),
      h('p', {
        class: 'ppl__leadtext',
        text:
          'Everybody who can use this register, and what each of them may do. An account is a ' +
          'username, its roles and a password: this screen is where all three are given, and ' +
          'the only place a role is granted or withdrawn.',
      }),
    ),
    deploymentCard(s),
    issuedCard(s),
    grantForm(s),
    peopleList(s),
    logCard(s),
    matrix(),
  );
}
